import { useEffect, useMemo, useRef, useState } from "react";
import { api, type TrackedTeam, type WindowTrackedResponse } from "../lib/api";
import { eventTier, windowState } from "../lib/events";
import { relativeTime } from "../lib/format";
import { hasImpostor } from "../lib/impostors";
import { useApp } from "../lib/store";
import { AddPlayers } from "./AddPlayers";
import { LiveBadge, Spinner } from "./bits";
import { Roster } from "./Roster";
import { TeamCard } from "./TeamCard";

const DASH_POLL_MS = 8000;
const CLOCK_TICK_MS = 30_000;

interface LiveWindow {
  windowId: string;
  eventId: string;
}

/** A stable per-team key. Board teams have a team_id; solo cards (a watched
 *  player not yet on the board) come back team_id=null with the player's
 *  display name as the label — which is NOT unique — so fall back to the
 *  watched member's epic_id, never the bare label. */
function teamKey(t: TrackedTeam): string {
  return t.team_id ?? t.members.find(m => m.watched)?.epic_id ?? t.label;
}

/** The home hero: "where are MY players right now?" for the active region.
 *
 *  We pass the client's own watch set (?ids=) to the per-window tracked
 *  endpoint, which synthesises each team from the board — so watching one
 *  member surfaces the whole duo — and surface them live-first, each one tap
 *  from its full window view. Nothing live yet → a calm idle roster. */
export function PlayersDashboard() {
  const { grouped, region, watchedIds } = useApp();
  const [picker, setPicker] = useState(false);

  // A slow clock so upcoming→live transitions are picked up without a reload
  // (the events catalog is fetched once, so Date.now() in the memos below
  // would otherwise be frozen at first load).
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), CLOCK_TICK_MS);
    return () => clearInterval(t);
  }, []);

  // Demoted "other" events (test/dev/ranked cups) never drive this dashboard:
  // a live test cup must not flip "Your players" into live-scanning mode and
  // bury the idle roster, nor count as the pros' "next match".
  const liveWindows = useMemo<LiveWindow[]>(() => {
    const out: LiveWindow[] = [];
    for (const g of grouped ?? []) {
      if (eventTier(g) === "other") continue;
      const rv = g.regions.find(r => r.region === region);
      if (!rv) continue;
      for (const w of rv.windows) {
        if (windowState(w, now) === "live")
          out.push({ windowId: w.event_window_id, eventId: rv.event_id });
      }
    }
    return out;
  }, [grouped, region, now]);

  const nextBegin = useMemo(() => {
    let min = Infinity;
    for (const g of grouped ?? []) {
      if (eventTier(g) === "other") continue;
      const rv = g.regions.find(r => r.region === region);
      if (!rv) continue;
      for (const w of rv.windows) {
        const b = Date.parse(w.begin_time);
        if (!Number.isNaN(b) && b > now && b < min) min = b;
      }
    }
    return Number.isFinite(min) ? min : null;
  }, [grouped, region, now]);

  // ── live: poll the tracked endpoint for each live window, scoped to my ids ──
  const [byWindow, setByWindow] = useState<Record<string, WindowTrackedResponse>>({});
  const [error, setError] = useState<string | null>(null);
  const liveKey = liveWindows.map(w => w.windowId).join(",");
  const idsKey = watchedIds.join(",");
  const liveRef = useRef(liveWindows);
  liveRef.current = liveWindows;

  useEffect(() => {
    setByWindow({});
    setError(null);
    const ids = idsKey ? idsKey.split(",") : [];
    if (liveRef.current.length === 0 || ids.length === 0) return;
    let cancelled = false;
    const load = async () => {
      // allSettled, not all: one flaky window must not blank the others.
      const settled = await Promise.allSettled(
        liveRef.current.map(w =>
          api.windowTracked(w.windowId, ids).then(r => [w.windowId, r] as const),
        ),
      );
      if (cancelled) return;
      const ok: Array<readonly [string, WindowTrackedResponse]> = [];
      for (const s of settled) if (s.status === "fulfilled") ok.push(s.value);
      if (ok.length > 0) {
        setByWindow(Object.fromEntries(ok));
        setError(null);
      } else {
        setError("failed to load players");
      }
    };
    load();
    const t = setInterval(load, DASH_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [liveKey, idsKey]);

  const teams = useMemo(() => {
    const map = new Map<string, { team: TrackedTeam; windowId: string }>();
    for (const w of liveWindows) {
      const resp = byWindow[w.windowId];
      if (!resp) continue;
      for (const t of resp.teams) {
        if (hasImpostor(t)) continue;
        const key = teamKey(t);
        const cur = map.get(key);
        if (!cur || (!cur.team.live && t.live)) map.set(key, { team: t, windowId: w.windowId });
      }
    }
    return [...map.values()].sort((a, b) => {
      if (!!a.team.live !== !!b.team.live) return a.team.live ? -1 : 1;
      return (a.team.rank ?? 999) - (b.team.rank ?? 999);
    });
  }, [byWindow, liveWindows]);

  const liveCount = teams.filter(t => t.team.live).length;
  const loaded = Object.keys(byWindow).length > 0;
  const isLive = liveWindows.length > 0;
  const empty = watchedIds.length === 0;

  return (
    <section className="mb-12">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="display flex items-center gap-2.5 text-xl text-fg sm:text-2xl">
            <span
              className={`h-2 w-2 rounded-full ${
                isLive && liveCount > 0 ? "pulse-dot bg-accent" : "bg-faint"
              }`}
            />
            Your players
          </h2>
          <p className="mt-1 font-mono text-[11.5px] text-faint">
            {region}
            {empty
              ? " · none yet"
              : isLive
                ? ` · ${liveCount} in a match`
                : nextBegin
                  ? ` · next match ${relativeTime(new Date(nextBegin).toISOString())}`
                  : " · no scheduled matches"}
          </p>
        </div>
        <div className="flex items-center gap-2.5">
          {isLive && liveCount > 0 && <LiveBadge />}
          <AddButton onClick={() => setPicker(true)} />
        </div>
      </div>

      {empty ? (
        <EmptyState onAdd={() => setPicker(true)} />
      ) : isLive ? (
        error && !loaded ? (
          <p className="chamfer bg-surface px-4 py-3 text-xs text-warn">{error}</p>
        ) : !loaded ? (
          <Spinner label="finding your players in live lobbies" />
        ) : teams.length === 0 ? (
          // Live window, but none of your pros are in a lobby yet — still show
          // the full roster (labelled "pending", i.e. scanning) instead of a
          // bare one-liner, so you keep visibility on who's being watched.
          <Roster status="pending" />
        ) : (
          <div className="grid items-start gap-4 lg:grid-cols-2">
            {teams.map(({ team, windowId }) => (
              <TeamCard key={teamKey(team)} team={team} windowLive windowId={windowId} />
            ))}
          </div>
        )
      ) : (
        <Roster status="idle" />
      )}

      <AddPlayers open={picker} onClose={() => setPicker(false)} />
    </section>
  );
}

function AddButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="press chamfer-sm bg-accent/12 px-3 py-1.5 font-mono text-[11px] font-medium uppercase tracking-wider text-accent transition-colors hover:bg-accent/20"
    >
      + Add players
    </button>
  );
}

function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="chamfer flex flex-col items-center gap-3 bg-surface px-6 py-10 text-center">
      <p className="display text-[15px] text-fg">No players yet</p>
      <p className="max-w-sm font-mono text-[11.5px] leading-relaxed text-faint">
        Add the pros you care about — their live matches and history surface the moment they play.
      </p>
      <button
        onClick={onAdd}
        className="press chamfer-sm mt-1 bg-accent/15 px-4 py-2 font-mono text-[11.5px] font-medium uppercase tracking-wider text-accent transition-colors hover:bg-accent/25"
      >
        + Add players
      </button>
    </div>
  );
}

